<template>
  <v-main>
    <v-container class="py-8">
      <v-card>
        <v-card-title>Prompts used to score AEO</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-alert
            v-if="!isAdmin"
            type="error"
            border="start"
            class="mb-6"
          >
            You must be an admin to view prompt details.
          </v-alert>

          <div v-else>
            <v-alert
              v-if="errorMessage"
              type="error"
              border="start"
              class="mb-6"
            >
              {{ errorMessage }}
            </v-alert>

            <v-row v-if="catalog.length">
              <v-col cols="12" md="3">
                <v-list nav density="comfortable" class="metric-list">
                  <v-list-item
                    v-for="metric in catalog"
                    :key="metric.name"
                    :value="metric.name"
                    :active="metric.name === selectedMetricName"
                    @click="selectedMetricName = metric.name"
                  >
                    <v-list-item-title>{{ metric.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-col>

              <v-col cols="12" md="9">
                <div v-if="selectedMetric">
                  <h3 class="text-h6 mb-2">{{ selectedMetric.label }}</h3>
                  <p v-if="selectedMetric.description" class="mb-4">{{ selectedMetric.description }}</p>

                  <!-- Submetrics section (only show if submetrics exist) -->
                  <div v-if="selectedMetric.submetrics && selectedMetric.submetrics.length > 0">
                    <h4 class="text-subtitle-1 mb-3">Submetrics</h4>
                    <v-expansion-panels>
                      <v-expansion-panel
                        v-for="sub in selectedMetric.submetrics"
                        :key="sub.name"
                      >
                        <v-expansion-panel-title>
                          {{ sub.label }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <p class="mb-3">{{ sub.description }}</p>
                          
                          <!-- Show calculation note for code-calculated metrics -->
                          <div v-if="sub.calculation_method === 'code'" class="mb-3">
                            <v-alert type="info" variant="tonal" density="compact" class="mb-2">
                              <strong>Calculation Method:</strong> This metric is calculated via code (not an LLM prompt).
                            </v-alert>
                            <v-card v-if="sub.calculation_note" variant="outlined" class="mt-2">
                              <v-card-text>
                                <strong>Calculation Details:</strong>
                                <pre class="prompt-block">{{ sub.calculation_note }}</pre>
                              </v-card-text>
                            </v-card>
                          </div>
                          
                          <!-- Show prompt for LLM-based metrics -->
                          <div v-if="sub.prompt">
                            <strong>Prompt:</strong>
                            <v-card class="mt-2" variant="outlined">
                              <v-card-text>
                                <pre class="prompt-block">{{ sub.prompt }}</pre>
                              </v-card-text>
                            </v-card>
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>

                  <!-- Prompt section (for generation prompts like persona/query) -->
                  <div v-if="selectedMetric.prompt">
                    <h4 class="text-subtitle-1 mb-3">Prompt</h4>
                    <v-card variant="tonal" class="mb-4">
                      <v-card-text>
                        <pre class="prompt-block">{{ selectedMetric.prompt }}</pre>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- Guidance section -->
                  <v-card v-if="selectedMetric.guidance" variant="tonal" class="mb-4 mt-6">
                    <v-card-title class="text-subtitle-2">Guidance</v-card-title>
                    <v-card-text>
                      <pre class="prompt-block">{{ selectedMetric.guidance }}</pre>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-else-if="!loading && !errorMessage"
              type="info"
              border="start"
            >
              No prompt definitions found.
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from '@/axios.js';
import { mapGetters } from 'vuex';

const BASE_URL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'Prompts',
  data() {
    return {
      catalog: [],
      selectedMetricName: null,
      loading: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapGetters(['isAdmin']),
    selectedMetric() {
      if (!this.catalog.length) return null;
      const found = this.catalog.find((metric) => metric.name === this.selectedMetricName);
      return found || this.catalog[0];
    },
  },
  methods: {
    async fetchCatalog() {
      this.loading = true;
      this.errorMessage = null;
      try {
        const response = await axios.get(`${BASE_URL}/user_admins/prompts`, {
          headers: this.authHeaders(),
        });
        this.catalog = response.data.metrics || [];
        if (this.catalog.length && !this.selectedMetricName) {
          this.selectedMetricName = this.catalog[0].name;
        }
      } catch (error) {
        this.errorMessage = this.extractError(error, 'Unable to load metrics.');
      } finally {
        this.loading = false;
      }
    },
    authHeaders() {
      return {
        Authorization: localStorage.getItem('auth_token'),
      };
    },
    extractError(error, fallback) {
      return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        fallback
      );
    },
  },
  async created() {
    if (!this.isAdmin) {
      this.errorMessage = 'You do not have permission to view this page.';
      return;
    }
    await this.fetchCatalog();
  },
};
</script>

<style scoped>
.prompt-block {
  white-space: pre-wrap;
  font-family: 'Fira Code', 'Source Code Pro', monospace;
  font-size: 0.85rem;
  margin: 0;
}
.metric-list {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
